"use client";

import React from "react";
import { Building2, Globe, Briefcase, FileText, Upload, ArrowRight, CircleCheck, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={cn("flex min-h-[80px] w-full rounded-none border-0 border-b-2 border-input bg-transparent px-0 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[#a855f7] disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300", className)} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
export default function BrandInquiries() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="33393" id="host-a-ritual" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/10 rounded-lg blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-secondary/10 rounded-lg blur-[120px]" />
      </div>

      <div ref={ref} className={cn("container mx-auto px-4 relative z-10 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {}
          <div className="lg:col-span-5 space-y-8 pt-4">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-6xl font-default font-bold tracking-tighter text-primary uppercase">
                Bring the Frequency to Your City
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed font-default tracking-wide">
                Invite the Yoga Dinners ritual to your community. We weave together movement, deep bass, 
                and sacred dining to create unforgettable collective experiences that awaken the spirit.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex gap-4 items-start">
                <div className="mt-1 bg-primary/10 p-3 border border-[#a855f7]">
                  <Globe className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-default font-bold text-lg text-primary tracking-widest uppercase">Sonic Alchemy</h3>
                  <p className="text-muted-foreground font-default">Curated deep bass tech house and high-vibration soundscapes designed for peak presence.</p>
                </div>
              </div>
              
              <div className="flex gap-4 items-start">
                <div className="mt-1 bg-primary/10 p-3 border border-[#a855f7]">
                  <Briefcase className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-default font-bold text-lg text-primary tracking-widest uppercase">Sacred Movement</h3>
                  <p className="text-muted-foreground font-default">Intuitive vinyasa flow guided by intentional teachers to open the body and senses.</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="mt-1 bg-primary/10 p-3 border border-[#a855f7]">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-default font-bold text-lg text-primary tracking-widest uppercase">Communal Ritual</h3>
                  <p className="text-muted-foreground font-default">Elevated dining experiences that nourish the soul and celebrate the power of connection.</p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-border/30">
              <p className="text-sm text-muted-foreground mb-4 font-serif italic tracking-wider">Seeking to join our global network of facilitators?</p>
              <Link to="#">
                <Button variant="outline" className="w-full sm:w-auto group border-[#a855f7] text-primary hover:bg-primary hover:text-primary-foreground tracking-widest uppercase text-xs transition-all duration-500">
                  Explore the Experience
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="bg-card/40 backdrop-blur-2xl border-border/30 shadow-2xl">
              <CardHeader className="pb-6 border-b border-border/30">
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className="w-5 h-5 text-primary/70" />
                  <span className="text-xs font-bold text-primary/70 uppercase tracking-[0.2em]">Partner Inquiries</span>
                </div>
                <CardTitle className="text-3xl font-default font-bold uppercase tracking-tight">Host a Ritual</CardTitle>
                <CardDescription className="text-muted-foreground text-base">
                  Tell us about your city and vision. Our production team will review your request and respond within 48 hours to begin the alchemy.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-8">
                <FormProvider formKey="host_ritual_form_global_gatherings" formId="host_ritual_form_global_gatherings" sectionName="brand-inquiries" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                brand_name: "",
                website: "",
                project_brief: "",
                budget_range: "",
                attachment: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                brand_name: {
                  required: true
                },
                project_brief: {
                  required: true
                }
              }} submitText="Send Request" successMessage="The signal has been received. Our team will reach out shortly." errorMessage="The transmission failed. Please try again or contact us directly.">
                  <BrandInquiryForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}
function BrandInquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-700">
        <div className="w-20 h-20 bg-primary/10 border border-[#a855f7] flex items-center justify-center mb-2">
          <CircleCheck className="w-10 h-10 text-primary" />
        </div>
        <h3 className="text-3xl font-default font-bold uppercase tracking-tight text-primary">Request Received</h3>
        <p className="text-muted-foreground max-w-md font-default tracking-wide">
          {successMessage}
        </p>
        <Button variant="outline" className="mt-6 border-[#a855f7] text-primary hover:bg-primary hover:text-primary-foreground tracking-widest uppercase text-xs" onClick={() => window.location.reload()}>
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest font-bold text-primary/80">Your Name <span className="text-secondary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Full Name" className="border-0 border-b-2 border-border bg-transparent focus-visible:border-[#a855f7] text-lg py-6 transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-wider text-muted-foreground/60">The primary contact for this gathering.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest font-bold text-primary/80">Email Address <span className="text-secondary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@ritual.com" className="border-0 border-b-2 border-border bg-transparent focus-visible:border-[#a855f7] text-lg py-6 transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Where we will send the proposal.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest font-bold text-primary/80">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (000) 000-0000" className="border-0 border-b-2 border-border bg-transparent focus-visible:border-[#a855f7] text-lg py-6 transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-wider text-muted-foreground/60">For direct coordination.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="brand_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest font-bold text-primary/80">City / Venue Name <span className="text-secondary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Ibiza, Spain" className="border-0 border-b-2 border-border bg-transparent focus-visible:border-[#a855f7] text-lg py-6 transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-wider text-muted-foreground/60">The location where the ritual will manifest.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="website" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest font-bold text-primary/80">Social / Website</FormLabel>
              <FormControl>
                <Input type="url" placeholder="https://instagram.com/..." className="border-0 border-b-2 border-border bg-transparent focus-visible:border-[#a855f7] text-lg py-6 transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Link to the venue or local community.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="budget_range" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest font-bold text-primary/80">Expected Attendance</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="border-0 border-b-2 border-border bg-transparent focus:ring-0 focus:border-[#a855f7] text-lg py-6 transition-all">
                    <SelectValue placeholder="Select scale" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Under $5,000">50 - 100 Guests</SelectItem>
                  <SelectItem value="$5,000 - $20,000">100 - 250 Guests</SelectItem>
                  <SelectItem value="$20,001 - $50,000">250 - 500 Guests</SelectItem>
                  <SelectItem value="Over $50,000">500+ Guests</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Helps us tailor the production energy.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="project_brief" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest font-bold text-primary/80">Your Vision <span className="text-secondary">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the space, the energy, and why your city needs the frequency..." className="min-h-[120px] resize-y text-lg" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Provide details on the potential space and local vibe.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...fieldProps
      }
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest font-bold text-primary/80">Moodboard / Deck (optional)</FormLabel>
            <FormControl>
              <div className="flex items-center gap-4">
                <Input type="file" className="cursor-pointer file:mr-4 file:py-2 file:px-6 file:rounded-none file:border-0 file:text-xs file:font-bold file:uppercase file:tracking-widest file:bg-primary file:text-primary-foreground hover:file:bg-primary/80 transition-all border-0 border-b-2 border-border bg-transparent" onChange={e => {
            onChange(e.target.files ? e.target.files[0] : null);
          }} {...fieldProps} />
              </div>
            </FormControl>
            <FormDescription className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Upload any relevant files (PDF, DOCX, JPG, PNG).</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-secondary/10 border border-secondary/20 animate-in fade-in slide-in-from-top-2">
          <p className="text-secondary text-sm font-bold uppercase tracking-widest flex items-center gap-2">
            <span className="text-lg"></span> {submitError}
          </p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-[0.2em] text-xs h-14 transition-all duration-500">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 h-4 w-4" />
              Building Frequency...
            </> : 'Send Request'}
        </Button>
      </div>
    </Form>;
}